/**
 * Contract domain schemas, derived from the generated Zod. The app-side
 * enrichments layered on top: decimal-string → number coercions, date
 * refinements, typed `customDetails`, and UI defaults.
 *
 * See ./README.md.
 */
import { z } from 'zod';

import { ContractsRetrieveResponse } from '~/data/generated/zod/contracts/contracts.zod';
import { PropertiesContractsListResponse } from '~/data/generated/zod/properties/properties.zod';
import { baseLinkedAttachmentSchema } from '~/data/schemas/attachment';
import { tolerantEnum } from '~/data/tolerantEnum';

import { decimalNumber, decimalNumberNullable, nullableRefinedDate } from './generatedZodHelpers';
import {
  type OneOrMore,
  VALID_DATE_YEAR_MESSAGE,
  validateDateYear,
  type Values,
} from './zodHelpers';

const contractAttachmentSchema = baseLinkedAttachmentSchema
  .omit({
    isNextPage: true,
  })
  .extend({
    isSummarySheet: z.boolean(),
    name: z.string().optional(),
    uploadedAt: z.string().nullable().optional(),
    hasTextLayer: z.boolean().optional(),
    kind: z.enum(['user_upload', 'cfi_report']).optional().default('user_upload'),
    // `pageNumber` nullability is inherited from `baseLinkedAttachmentSchema`.
  });

export const ContractType = {
  Full: '24/7',
  Standard: 'standard',
  StandardOt: 'standard-ot-differential',
  PlatinumOt: 'platinum-ot-differential',
  Other: 'other',
};
type ContractTypeKey = Values<typeof ContractType>;
export const ContractTypeValues = Object.values(ContractType) as OneOrMore<ContractTypeKey>;

export const ContractTypeOptions = {
  [ContractType.Full]: '24/7',
  [ContractType.Standard]: 'Standard',
  [ContractType.StandardOt]: 'Standard OT Differential',
  [ContractType.PlatinumOt]: 'Platinum OT Differential',
  [ContractType.Other]: 'Other',
};

const contractRateSchema = z.object({
  id: z.string(),
  workType: z.string(),
  workerType: z.string(),
  rateType: z.string(),
  coverageType: z.string(),
  isDefault: z.boolean(),
  description: z.string(),
  // DRF decimal string → number; the UI passes these straight to formatCurrency.
  hourlyRate: decimalNumber,
  overtimeMultiplier: decimalNumber.nullable().optional(),
});

/**
 * A rate set on a contract read. The wire has NO `contract` key — requiring one
 * throws on every contract that has a rate set. `isNew` is a UI-only flag.
 */
const contractRateSetSchema = ContractsRetrieveResponse.shape.rateSets.element.extend({
  isNew: z.boolean().default(false),
  rates: z.array(contractRateSchema).default([]),
});

const contractReviewRuleSchema = z.object({
  id: z.string(),
  reviewRule: z.string().min(1, 'Review rule is required'),
  category: z
    .enum([
      'review-rule',
      'included-service',
      'excluded-service',
      'miscellaneous-charge',
      'other-term',
    ])
    .default('review-rule'),
});

const createUpdateContractReviewRuleSchema = contractReviewRuleSchema.omit({
  id: true,
});

const createUpdateContractRateSchema = contractRateSchema
  .omit({
    id: true,
  })
  .partial();

export const createUpdateContractRateSetSchema = contractRateSetSchema
  .omit({
    // `contract` no longer exists on the derived read schema (it was never on the
    // wire). `createdAt`/`updatedAt` are server-owned read-only fields the write
    // payload does not carry, so drop them from the create/update shape.
    rates: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    // The create form assigns temporary ids like `new:0` to rate sets the user
    // just added, so the WRITE shape accepts any string (the read inherits the
    // generated `.uuid()`).
    id: z.string(),
    rates: z.array(createUpdateContractRateSchema),
  });

const contractReviewRulesSchema = z.object({
  id: z.string().optional(),
  reviewRule: z.string(),
  category: z
    .enum([
      'review-rule',
      'included-service',
      'excluded-service',
      'miscellaneous-charge',
      'other-term',
    ])
    .default('review-rule'),
});

const contractPropertyCarSchema = z.object({
  id: z.string(),
  carId: z.string(),
  name: z.string(),
  maintenanceQuantity: decimalNumber,
  maintenanceUnit: z.enum(['visits', 'hours']),
  maintenanceTimePeriod: decimalNumber,
  regularCallbackRate: decimalNumber,
  emergencyCallbackRate: decimalNumber,
  regularCallbackResponseTime: decimalNumber,
  emergencyCallbackResponseTime: decimalNumber,
});

/**
 * A property under a contract. Only `cars` is overridden, to coerce its
 * decimal-string KPI targets to numbers.
 */
const contractPropertySchema = ContractsRetrieveResponse.shape.propertyContracts.element.extend({
  cars: z.array(contractPropertyCarSchema),
  // A property under a contract can have no address on record and no rate set
  // assigned; the nested serializer types both non-null.
  propertyAddress: z.string().nullable(),
  rateSetId: z.string().nullable(),
});

// Free-form "Add your own Summary Item" entries shown on the contract
// details view. Persisted on Contract.customDetails as an array of
// {name, label, value} objects. The generated read types this as `unknown`
// (arbitrary JSON), so the typed shape is a genuine app-side enrichment.
export const contractCustomDetailEntrySchema = z.object({
  name: z.string(),
  label: z.string(),
  value: z.string(),
});

/** The contract read schema. */
export const contractSchema = ContractsRetrieveResponse.extend({
  // serviceProvider (and its nested `owner`) inherit the generated base: both
  // are `allow_null=True` at source as of AMP-633, so the generated
  // `.nullable()` already matches the null a provider-less contract (or a
  // provider group with no linked account) emits.

  // The generated `type` mirrors Contract.ContractType.choices, but Django's
  // `choices` is NOT a DB constraint, and the column carries no default
  // (`type = models.CharField(max_length=50, choices=ContractType.choices)`),
  // so a row that was never given a type holds `''`. That is the value actually
  // observed in production: AMP-619 runtime testing recorded `Contract.type` as
  // an empty string outside ContractTypeEnum (services/api/main/tests/
  // schema_exemptions.py, `contractsPartialUpdate`). The strict generated enum
  // then rejected such a contract on READ, dropping the response to the raw
  // tier ("Contract.*.type (invalid_enum_value)", AMP-636).
  //
  // Not the extraction path, despite the shape of the bug: approval.py maps
  // display labels back to stored values and then runs
  // `ContractWriteSerializer(...).is_valid(raise_exception=True)`, whose
  // ChoiceField rejects anything out of enum, so an LLM string cannot land in
  // the column today. (Extraction is only guarded by that serializer, not at
  // the extraction layer — approval.py carries a TODO to constrain the model
  // with structured outputs. Worth doing; not the cause here.)
  //
  // So: accept any string on read and preserve it verbatim — the UI renders
  // `contract.type` as-is — but REPORT the out-of-enum value, or widening the
  // type would make genuine corruption permanently invisible. The write path
  // (`CreateContract.type`) stays a strict enum. The edit form does NOT coerce
  // such a value to `Other`: `ContractEditDialog` spreads `type` through
  // verbatim and `ContractForm.type` is `z.enum(ContractTypeValues)`, so
  // opening Edit on one of these contracts shows a BLANK Type select and Save
  // is blocked with an invalid-enum error until the user picks a real type.
  // (Coercing to `Other` is Django *admin* import behaviour —
  // `ValidatingContractTypeWidget` in main/admin/contract.py — not the webapp.)
  type: tolerantEnum(ContractsRetrieveResponse.shape.type, {
    kind: 'Contract',
    path: 'type',
  }),

  // DRF DecimalField reads: adopt as numbers the UI can do math on.
  partsAndMaterialsMarkup: decimalNumberNullable,
  hourlyFeeAdjustmentRate: decimalNumberNullable,
  // Nullish on the wire (generated `.nullish()`) — the key can be absent.
  uptimeTarget: decimalNumberNullable.optional(),
  onTimeResponseTimeTargetPct: decimalNumberNullable.optional(),
  // Nullable but always present (generated `.nullable()`, not `.nullish()` —
  // list and retrieve share ContractSerializer, so the key is never dropped);
  // do NOT add `.optional()`, which would mask a backend that dropped the key.
  annualEscalationRate: decimalNumberNullable,

  // DateField(null=True) reads; keep the app's 1900–2100 year refinement.
  startDate: nullableRefinedDate,
  expirationDate: nullableRefinedDate,

  // Typed view over the generated `unknown` JSON blob.
  customDetails: z.array(contractCustomDetailEntrySchema).default([]),

  // Nullable but always present on the wire (generated `.nullable()`, not
  // `.nullish()`): list and retrieve both use ContractSerializer with identical
  // Meta.fields, so these keys are never dropped. Inherit the generated
  // nullability as-is — no `.optional()`, which would silently swallow a
  // future backend that stopped emitting the key instead of throwing.
  monthlyPricingTaxInclusive: ContractsRetrieveResponse.shape.monthlyPricingTaxInclusive,
  extractionVersion: ContractsRetrieveResponse.shape.extractionVersion,
  extractionAppliedAt: ContractsRetrieveResponse.shape.extractionAppliedAt,

  // UI defaults for fields the payload may omit.
  aiReviewEligible: z.boolean().default(false),
  propertyContracts: z.array(contractPropertySchema).default([]),

  // App-composed nested collections (coercions + defaults the wire lacks).
  attachments: z.array(contractAttachmentSchema),
  reviewRules: z.array(contractReviewRulesSchema),
  rateSets: z.array(contractRateSetSchema),
});

const propertyContractCarSchema = z.object({
  id: z.string().optional(),
  carId: z.string().readonly(),
  name: z.string().readonly(),
  ahjId: z.string().readonly(),
  // Car.elevator_type is TextField(null=True): a car with no recorded elevator
  // type serializes null, and the generated cars[].elevatorType is a union
  // `.nullable()`. The old non-null string threw at the read boundary.
  elevatorType: z.string().readonly().nullable(),
  maintenanceQuantity: decimalNumber,
  maintenanceUnit: z.enum(['visits', 'hours']),
  maintenanceTimePeriod: decimalNumber,
  regularCallbackRate: decimalNumber,
  emergencyCallbackRate: decimalNumber,
  regularCallbackResponseTime: decimalNumber,
  emergencyCallbackResponseTime: decimalNumber,
});

/**
 * A standalone property-contract row. `monthlyPrice` stays nullable: the model
 * field is `null=True` even though the generated standalone serializer types it
 * non-null.
 */
export const propertyContractSchema = PropertiesContractsListResponse.shape.results.element.extend({
  id: z.string(),
  // monthly_price is DecimalField(null=True); the generated standalone serializer
  // omits the nullability the nested contract read declares, so add it here.
  monthlyPrice: z.string().nullable(),
  cars: z.array(propertyContractCarSchema),
  startDate: nullableRefinedDate,
  expirationDate: nullableRefinedDate,
});
export type PropertyContract = z.infer<typeof propertyContractSchema>;

// canChat/numCars are backend-computed read fields (a SerializerMethodField and
// a read_only IntegerField in PropertyContractSerializer; neither is a DB
// column), never accepted on write. propertyContractSchema requires them
// because it derives from the READ element (PropertiesContractsListResponse),
// so the write shape must omit them explicitly or every save fails — the form
// never carries them through the resolver in the first place (AMP-630).
const createPropertyContractSchema = propertyContractSchema.omit({
  id: true,
  contractId: true,
  canChat: true,
  numCars: true,
});

export const createContractAttachmentSchema = z.object({
  id: z.string(),
  isPrimary: z.boolean().default(true),
  pageNumber: z.number().default(1),
  isSummarySheet: z.boolean().default(true),
});

export const CreateContract = z.object({
  name: z.string(),
  // The FK is nullable on the backend; the form's "None" option is
  // normalized to null before reaching this schema (an empty string is not
  // a valid FK value).
  serviceProvider: z.string().nullable(),
  startDate: z.string().refine(validateDateYear, { message: VALID_DATE_YEAR_MESSAGE }),
  expirationDate: z.string().refine(validateDateYear, { message: VALID_DATE_YEAR_MESSAGE }),
  term: z.string(),
  type: z.enum(ContractTypeValues),
  contractPaper: z.string(),
  businessHours: z.string(),
  phoneMonitoring: z.boolean().nullable(),
  fireService: z.boolean().nullable(),
  pressureTest: z.boolean().nullable(),
  partsAndMaterialsMarkup: z.coerce.number(),
  cancellation: z.string(),
  maintenanceCompletionTarget: z.coerce.number().optional(),
  maintenanceHoursTarget: z.coerce.number().optional(),
  uptimeTarget: z.coerce.number().optional(),
  onTimeResponseTime: z.coerce.number().optional(),
  emergencyCallRateTarget: z.coerce.number().optional(),
  callbackResponseTime: z.coerce.number().optional(),
  emergencyCallbackResponseTime: z.coerce.number().optional(),
  hourlyFeeAdjustmentRate: z.coerce.number(),
  notes: z.string(),
  customDetails: z.array(contractCustomDetailEntrySchema).optional(),
  attachments: z.array(createContractAttachmentSchema),
  propertyContracts: z.array(createPropertyContractSchema),
  rateSets: z.array(createUpdateContractRateSetSchema),
  reviewRules: z.array(createUpdateContractReviewRuleSchema),
});

export const UpdateContract = CreateContract.extend({
  id: z.string(),
})
  .omit({
    propertyContracts: true,
    attachments: true,
    reviewRules: true,
    rateSets: true,
  })
  .extend({
    propertyContracts: z.array(
      createPropertyContractSchema.extend({
        id: z.string().optional(),
      }),
    ),
    reviewRules: z.array(
      createUpdateContractReviewRuleSchema.extend({
        id: z.string().optional(),
      }),
    ),
    rateSets: z.array(
      createUpdateContractRateSetSchema.extend({
        id: z.string().optional(),
      }),
    ),
  });

export type Contract = z.infer<typeof contractSchema>;
export type CreateContract = z.infer<typeof CreateContract>;
export type UpdateContract = z.infer<typeof UpdateContract>;
